import { QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ProcessOfferDto, ProcessOfferItemDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';

export const OFFER_LOCKED_ERROR_CODE = 'OFFER_LOCKED';

export const isOfferLockedError = (error: unknown): boolean =>
  ((error as Partial<AxiosError>)?.response?.data as { code?: string } | undefined)?.code ===
  OFFER_LOCKED_ERROR_CODE;

export const refreshOfferQueries = async (queryClient: QueryClient): Promise<void> => {
  await Promise.all(
    [
      QueryKeyEnum.PROCESS.OFFER_DETAILS,
      QueryKeyEnum.PROCESS.OFFER_STATUS_ALLOWED_TRANSITIONS,
      QueryKeyEnum.PROCESS.MULTI_OFFER_DETAILS,
      QueryKeyEnum.PROCESS.MULTI_OFFERS,
      QueryKeyEnum.PROCESS_OFFERS.SEARCH,
    ].map(key => queryClient.invalidateQueries({ queryKey: [key] })),
  );
};

export const isOfferLocked = (offer?: Pick<ProcessOfferDto, 'sentAt'> | null): boolean =>
  Boolean(offer?.sentAt);

export type OfferVersion = Pick<
  ProcessOfferItemDto,
  'name' | 'processOfferId' | 'sentAt' | 'status' | 'versionCorrelationId' | 'versionNumber'
>;

export type OfferVersionChain = {
  current?: OfferVersion;
  latest?: OfferVersion;
  nextVersionNumber: number;
  versions: OfferVersion[];
};

export const resolveOfferVersionChain = (
  offer: Pick<ProcessOfferDto, 'processOfferId' | 'versionCorrelationId' | 'versionNumber'>,
  siblings: OfferVersion[],
): OfferVersionChain => {
  const versions = siblings
    .filter(it => it.versionCorrelationId === offer.versionCorrelationId)
    .sort((a, b) => a.versionNumber - b.versionNumber);
  const latest = versions.at(-1);
  const highest = Math.max(offer.versionNumber, latest?.versionNumber ?? 0);
  return {
    current: versions.find(it => it.processOfferId === offer.processOfferId),
    latest,
    nextVersionNumber: highest + 1,
    versions,
  };
};
